import React from 'react';

import { Botao, Chip, Modal, Nota } from '../../../ui';
import { Associado } from '../../../api/associado/associadoApi';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { isCarroVivo } from '../../../utils/carroSituacao';
import { NOTA_ENVIO_AUTOMATICO } from '../../../utils/envioSgaRegras';

export interface ModalDetalhesAdesaoProps {
  /** Associado em exibição — `null` mantém o modal fechado. */
  associado: Associado | null;
  /** `produtosVinculados[carroId]` — o mesmo mapa que a tabela já usava. */
  produtosVinculados: Record<string, boolean>;
  verificando: boolean;
  onFechar: () => void;
  onVerificarNoSga: () => void;
}

/**
 * Detalhes de uma adesão (at-54; reescrito na at-70, frente D/D8).
 *
 * Decisão de Victor (08/09/2026) que ele atende: *"não gosto de tabelas em
 * mobile"*. A tabela de `/adesoes` tinha 6 colunas, três delas **bolinhas coloridas
 * sem texto**; hoje a linha é um índice enxuto (nome + CPF) e **o estado completo
 * vive aqui**, com texto em vez de cor.
 *
 * ## O que mudou na at-70
 *
 *   1. **Virou o `Modal` do kit**, como o irmão `ModalDetalhesCarteira` (at-65): o
 *      overlay era o `.filtros-overlay` de outro componente, com `Escape` próprio,
 *      `aria-modal="true"` **sem prender o foco** e um `×` que era caractere
 *      literal.
 *   2. **O código do SGA saiu do `title`** e virou `Chip` com o rótulo por extenso.
 *   3. Os chips passaram a ser os do kit — pendência em **âmbar**, não no laranja
 *      da marca.
 *
 * 📌 **Migrado nesta branch por causa do CSS, não por escopo de tela.** Ele é o
 * último consumidor real de `ModalFiltrosEnvios.css` e de `.envios-acao-btn` fora
 * de `/ti/envios`: sem migrá-lo, aqueles arquivos ficariam vivos por um modal só.
 * `/adesoes` (a tela que o abre) não foi tocada — o contrato de props é o mesmo.
 */
const ModalDetalhesAdesao: React.FC<ModalDetalhesAdesaoProps> = ({
  associado,
  produtosVinculados,
  verificando,
  onFechar,
  onVerificarNoSga,
}) => {
  if (!associado) return null;

  // Só veículos em circulação: o substituído continua no registro (at-37) e
  // contá-lo como pendente faria a tela acusar envio que não existe mais.
  const carros = (associado.carros ?? []).filter(isCarroVivo);
  const primeiroCarro = carros[0];

  const veiculoEnviado = carros.length > 0 && carros.every((carro) => carro.addedToSga);
  const veiculoParcial = carros.some((carro) => carro.addedToSga) && !veiculoEnviado;

  return (
    <Modal
      aberto
      titulo={associado.nomeCompleto}
      descricao={formatCpfCnpj(String(associado.cpfCnpj || ''))}
      tamanho="lg"
      aoFechar={onFechar}
    >
      <div className="tw-flex tw-flex-col tw-gap-6">
        <section>
          <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
            Situação no SGA
          </h3>

          {associado.codigoAssociado && (
            <div className="tw-mb-2">
              {/* Era `title="Código do associado no SGA"` sobre um chip escrito
                  "SGA 12345" — o rótulo agora está no próprio texto. */}
              <Chip variante="neutro">Código no SGA: {associado.codigoAssociado}</Chip>
            </div>
          )}

          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
            <LinhaEstado
              rotulo="Associado"
              enviado={associado.addedToSga === true}
              textoPendente="Ainda não enviado"
            />
            <LinhaEstado
              rotulo="Veículo"
              enviado={veiculoEnviado}
              textoPendente={
                carros.length === 0
                  ? 'Nenhum veículo em circulação'
                  : veiculoParcial
                    ? 'Parte dos veículos ainda não enviada'
                    : 'Ainda não enviado'
              }
            />
            <LinhaEstado
              rotulo="Produtos"
              enviado={!!primeiroCarro?.id && !!produtosVinculados[primeiroCarro.id]}
              textoPendente={primeiroCarro?.id ? 'Ainda não vinculados' : 'Depende do veículo'}
            />
          </ul>

          {/* O envio é automático — dizer isso evita que pendência vire susto
              (mesma legenda do painel `/vistoria`, at-24). */}
          <Nota className="tw-mt-3">{NOTA_ENVIO_AUTOMATICO}</Nota>
        </section>

        {carros.length > 0 && (
          <section>
            <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
              {carros.length === 1 ? 'Veículo' : `Veículos (${carros.length})`}
            </h3>

            <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
              {carros.map((carro) => (
                <li
                  key={carro.id}
                  className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2"
                >
                  <span className="tw-font-marca tw-text-sm tw-font-bold tw-tracking-wider tw-text-texto-titulo">
                    {carro.placa}
                  </span>
                  {/* A situação não entra aqui: a lista já é filtrada por
                      `isCarroVivo`, então seria sempre "Ativo". O que varia — e o
                      que o consultor quer saber — é o envio ao SGA. */}
                  <Chip variante={carro.addedToSga ? 'ok' : 'pendente'}>
                    {carro.addedToSga ? 'No SGA' : 'Aguardando envio'}
                  </Chip>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div>
          <Botao variante="primario" carregando={verificando} onClick={onVerificarNoSga}>
            Verificar no SGA
          </Botao>
        </div>
      </div>
    </Modal>
  );
};

/** Uma linha de estado: texto, não cor — a bolinha sozinha não se explica. */
const LinhaEstado: React.FC<{ rotulo: string; enviado: boolean; textoPendente: string }> = ({
  rotulo,
  enviado,
  textoPendente,
}) => (
  <li className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
    <span className="tw-text-sm tw-text-texto-corpo">{rotulo}</span>
    <Chip variante={enviado ? 'ok' : 'pendente'}>{enviado ? 'Enviado' : textoPendente}</Chip>
  </li>
);

export default ModalDetalhesAdesao;
